import React, { useState } from 'react';
import type { AdminTransferDetail } from '@platform/shared-types';
import { businessTransferApi } from '../../../services/businessTransferApi';
import { toastService } from '../../../services/toastService';
import { when } from '../format';
import { runWarningsOf } from './RunLogPanel';

type P = { detail: AdminTransferDetail; canAct: boolean; onChange: (d: AdminTransferDetail) => void };

export default function CompletePanel({ detail, canAct, onChange }: P) {
  const t = detail.transfer;
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState(false);
  if (t.completedAt) return <div className="text-sm text-emerald-800">Completed {when(t.completedAt)}.</div>;
  const open = detail.stages.filter((s) => s.id !== 'complete' && s.state !== 'done' && s.state !== 'skipped');
  // F1: every run warning is listed, and Complete stays off until someone says they were handled.
  const warnings = runWarningsOf(detail);
  const needsAck = warnings.length > 0;
  return (
    <div className="flex flex-col gap-2 text-sm">
      {open.length > 0 && <div className="text-stone-600">Still open: {open.map((s) => s.label).join(', ')}</div>}
      {needsAck && (
        <div className="rounded-lg border border-amber-300 border-l-4 border-l-[#DC143C] bg-amber-50 p-3 text-amber-900">
          <div className="mb-1 font-semibold">{warnings.length} run warning{warnings.length === 1 ? '' : 's'} to handle before completing</div>
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {warnings.map((w, i) => (
              <li key={`${w.stepId}-${w.code}-${i}`}><span className="text-stone-600">{w.step}:</span> <span className="font-mono font-semibold">{w.code}</span> — {w.message}</li>
            ))}
          </ul>
        </div>
      )}
      {canAct && needsAck && (
        <button type="button" role="checkbox" aria-checked={handled} onClick={() => setHandled((v) => !v)}
          className="flex min-h-[44px] items-center gap-3 self-start rounded-lg border border-stone-300 bg-white px-3 text-left text-stone-800">
          <span aria-hidden className={`flex h-5 w-5 items-center justify-center rounded border-2 ${handled ? 'border-[#DC143C] bg-[#DC143C] text-white' : 'border-stone-400 bg-white'}`}>
            {handled ? '✓' : ''}
          </span>
          I&apos;ve handled these warnings
        </button>
      )}
      {canAct && (
        <button type="button" disabled={busy || open.length > 0 || (needsAck && !handled)} className="min-h-[44px] self-start rounded-lg bg-[#DC143C] px-4 font-semibold text-white disabled:opacity-40"
          onClick={async () => {
            setBusy(true);
            try { onChange(await businessTransferApi.complete(t.transferId, { acknowledgeWarnings: needsAck && handled })); toastService.success('Transfer complete'); }
            catch (e: any) { toastService.error(e.message); } finally { setBusy(false); }
          }}>
          Complete transfer
        </button>
      )}
    </div>
  );
}
